import { useCallback, useEffect, useRef, useState } from "react";
import { creativeLibraryApi } from "@/lib/api/creative-library";
import { useAuth } from "@/lib/config/auth-provider";
import {
  creativeLibraryKey,
  creativeLibrarySnapshotGeneration,
  readCreativeLibrarySnapshot,
  writeCreativeLibrarySnapshot,
} from "./creativeLibrarySnapshot";
import type { CreativeLibraryData } from "./creativeLibrarySnapshot";

interface LibraryView {
  key: string | null;
  data: CreativeLibraryData | null;
  fetching: boolean;
  error: boolean;
}

const EMPTY_LIBRARY: CreativeLibraryData = {
  subjects: [],
  brands: [],
  products: [],
  boards: [],
  creatives: [],
};

/**
 * `loading` is true only with nothing to show. With a last good result, cards
 * stay on screen and `refreshing` covers the request behind them. Anything that
 * reads absence as a fact, such as "unavailable", waits for `!refreshing`.
 */
export function useCreativeLibrary(enabled = true) {
  const { user } = useAuth();
  const key = creativeLibraryKey(user?.id, user?.organizationToken);
  const [stored, setStored] = useState<LibraryView>(() => ({
    key,
    data: readCreativeLibrarySnapshot(key),
    fetching: enabled,
    error: false,
  }));
  const latestRequest = useRef(0);
  // Another identity's view never paints, not even for the render before the effect.
  const view: LibraryView =
    stored.key === key
      ? stored
      : {
          key,
          data: readCreativeLibrarySnapshot(key),
          fetching: enabled,
          error: false,
        };
  const reload = useCallback(async () => {
    if (!enabled) return;
    latestRequest.current += 1;
    const request = latestRequest.current;
    const startedAt = creativeLibrarySnapshotGeneration();
    setStored((current) => ({
      key,
      data:
        (current.key === key ? current.data : null) ??
        readCreativeLibrarySnapshot(key),
      fetching: true,
      error: false,
    }));
    try {
      const [
        nextBrands,
        nextProducts,
        nextBoards,
        nextCreatives,
        nextSubjects,
      ] = await Promise.all([
        creativeLibraryApi.brands(),
        creativeLibraryApi.products(),
        creativeLibraryApi.boards(),
        creativeLibraryApi.creatives(),
        creativeLibraryApi.subjects(),
      ]);
      if (request !== latestRequest.current) return;
      const data: CreativeLibraryData = {
        subjects: nextSubjects,
        brands: nextBrands,
        products: nextProducts,
        boards: nextBoards,
        creatives: nextCreatives,
      };
      writeCreativeLibrarySnapshot(key, data, startedAt);
      setStored({ key, data, fetching: false, error: false });
    } catch {
      if (request !== latestRequest.current) return;
      setStored((current) => ({ ...current, fetching: false, error: true }));
    }
  }, [enabled, key]);
  useEffect(() => {
    void reload();
  }, [reload]);
  const hasData = Boolean(view.data);
  return {
    ...(view.data ?? EMPTY_LIBRARY),
    loading: view.fetching && !hasData,
    refreshing: view.fetching && hasData,
    hasData,
    error: view.error,
    reload,
  };
}
